import { useState } from "react";
import { Check, Copy } from "lucide-react";

/**
 * High-contrast code box for the developer hub. Colors are hard-set (light text
 * on a dark slab) so the snippet is always readable in light and dark themes.
 */
export function CodeBox({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  }
  return (
    <div className="relative rounded-lg border border-amber-500/25 overflow-hidden" style={{ background: "#0b0d18" }}>
      {label && (
        <div className="px-3 pt-2 text-[10px] uppercase tracking-wider" style={{ color: "#8e94ad" }}>
          {label}
        </div>
      )}
      <pre className="overflow-x-auto p-3 pr-24 text-[12px] leading-relaxed" style={{ color: "#e9ecf5" }}>
        <code className="font-mono whitespace-pre-wrap break-all" style={{ color: "#e9ecf5" }}>
          {code}
        </code>
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-bold hover:opacity-90"
        style={{ color: "#12131f" }}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied" : "Copy code"}
      </button>
    </div>
  );
}

export function CodeTabs({ tabs }: { tabs: { id: string; label: string; code: string; note?: string }[] }) {
  const [active, setActive] = useState(tabs[0]?.id ?? "");
  const tab = tabs.find((t) => t.id === active) ?? tabs[0];
  if (!tab) return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActive(t.id)}
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition ${
              t.id === tab.id
                ? "bg-amber-500 text-background border-amber-500"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab.note && <p className="text-xs text-muted-foreground">{tab.note}</p>}
      <CodeBox code={tab.code} />
    </div>
  );
}
